import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, FilmSlate, DeviceMobile, FrameCorners, Sparkle } from "@phosphor-icons/react";
import { Reveal, SectionHeading } from "./Reveal";
import { PROJECTS, COMING_SOON, CATEGORIES } from "@/data/content";

const CATEGORY_ICONS = { FilmSlate, DeviceMobile, FrameCorners, Sparkle };

function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isYouTube = project.video && /youtube|youtu\.be/.test(project.video);

  return (
    <motion.div
      data-testid="project-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 bg-black/85 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl border border-gold/20 bg-void shadow-[0_0_80px_rgba(229,169,60,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="modal-close-button"
          aria-label="Close project"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full glass border border-white/15 text-zinc-300 hover:text-gold hover:border-gold/50 transition-colors duration-300"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="overflow-hidden rounded-t-3xl bg-surface">
          {project.video ? (
            isYouTube ? (
              <div className="aspect-video w-full">
                <iframe
                  src={project.video}
                  title={project.title}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <video src={project.video} controls autoPlay className="w-full aspect-video object-cover" data-testid="modal-video" />
            )
          ) : (
            <img src={project.image} alt={`${project.title} — project visual`} className="w-full aspect-video object-cover" data-testid="modal-image" />
          )}
        </div>

        <div className="p-6 md:p-10">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-3.5 py-1.5">
            {project.category}
          </span>
          <h3 className="mt-4 font-display text-2xl md:text-4xl font-bold text-zinc-50" data-testid="modal-title">
            {project.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed max-w-2xl">{project.desc}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Work() {
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);
  const hasProjects = PROJECTS.length > 0;
  const filtered = hasProjects
    ? active === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === active)
    : active === "All" ? COMING_SOON : COMING_SOON.filter((c) => c.category === active);

  return (
    <section id="work" data-testid="work-section" className="relative py-28 md:py-44 bg-surface/40">
      <div className="max-w-[88rem] mx-auto px-6 md:px-10">
        <SectionHeading chapter="03" overline="Selected Work" title="My Work" />

        <Reveal className="mb-10 flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap gap-2 md:gap-3" data-testid="work-filter-bar">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase()}`}
                onClick={() => setActive(cat)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 border ${
                  active === cat
                    ? "bg-gold/10 border-gold/50 text-gold"
                    : "border-white/10 text-zinc-400 hover:text-zinc-100 hover:border-white/25"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-600" data-testid="work-count">
            {hasProjects ? `${filtered.length} project${filtered.length === 1 ? "" : "s"}` : "Work in progress"}
          </p>
        </Reveal>

        <motion.div layout className="grid md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-24" data-testid="work-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => (
              <motion.article
                key={hasProjects ? item.id : item.category}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                data-testid={hasProjects ? `project-card-${item.id}` : `coming-soon-${item.category.toLowerCase()}`}
                className={`group relative ${i % 2 === 1 ? "md:mt-16" : ""}`}
              >
                <div className="flex items-end justify-between mb-5">
                  <span className="font-display text-6xl md:text-7xl font-black leading-none text-stroke-gold select-none" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-3.5 py-1.5">
                    {item.category}
                  </span>
                </div>

                {hasProjects ? (
                  <>
                    <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] group-hover:border-gold/50 transition-colors duration-500 group-hover:shadow-[0_0_60px_rgba(229,169,60,0.12)]">
                      <img
                        src={item.image}
                        alt={`${item.title} — ${item.category} project thumbnail`}
                        loading="lazy"
                        className="w-full aspect-[16/10] object-cover scale-105 group-hover:scale-100 transition-transform duration-[1.4s] ease-out"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-void via-void/60 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                        <h3 className="font-display text-2xl md:text-3xl font-bold text-zinc-50 group-hover:text-gold transition-colors duration-300">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-md">{item.desc}</p>
                      </div>
                    </div>
                    <button
                      data-testid={`view-project-${item.id}`}
                      onClick={() => setSelected(item)}
                      className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-gold hover:text-gold-soft transition-colors duration-300 group/btn"
                    >
                      View Project
                      <ArrowUpRight size={15} weight="bold" className="transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="relative overflow-hidden rounded-2xl border border-dashed border-white/[0.12] bg-surface aspect-[16/10] flex items-center justify-center group-hover:border-gold/30 transition-colors duration-500">
                      <div className="absolute inset-0 glow-gold opacity-40 blur-[80px]" />
                      <div className="relative flex flex-col items-center gap-5 px-6 text-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
                          {(() => { const Icon = CATEGORY_ICONS[item.icon]; return <Icon size={26} weight="duotone" />; })()}
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-gold" data-testid={`coming-soon-badge-${item.category.toLowerCase()}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse-dot" />
                          Coming Soon
                        </span>
                      </div>
                    </div>
                    <h3 className="mt-5 font-display text-2xl md:text-3xl font-bold text-zinc-50">{item.title}</h3>
                    <p className="mt-2 text-sm text-zinc-500 leading-relaxed max-w-md">{item.blurb}</p>
                  </>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
